import React from 'react';
import { Alert, Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import type { ImageBytes } from '../QualityCheck/imageMeta';
import { formatSubmissionDateTime } from '../../../utils/dateUtils';
import { lesionTitle, PhotoToPlace, PlacementLesion, UnassignedImageDto } from './photoPlacement';
import PlacementPhoto from './PlacementPhoto';
import LesionFacts from './LesionFacts';

const T = 'hospital.patient.photo-placement';

export type PhotoPlacementState =
    | { kind: 'loading' }
    | { kind: 'error' }
    /** `lesions` holds only the lesions of the submission on screen; empty when it does not speak for the visit */
    | { kind: 'ready'; lesions: PlacementLesion[]; unassigned: UnassignedImageDto[] };

interface PhotoPlacementPanelProps {
    state: PhotoPlacementState;
    /** decrypted bytes per photo `localId` */
    bytesByLocalId: Record<string, ImageBytes>;
    onRetry: () => void;
    onRetryImage: (localId: string) => void;
    onMove: (photo: Extract<PhotoToPlace, { kind: 'move' }>) => void;
    onAttach: (photo: UnassignedImageDto) => void;
}

function PhotoWithAction({ bytes, onRetry, action }: { bytes: ImageBytes | undefined; onRetry: () => void; action: React.ReactNode }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
            <PlacementPhoto bytes={bytes} onRetry={onRetry} />
            {action}
        </Box>
    );
}

/**
 * SKIN-576 — a closed visit's photos, lesion by lesion, and the photos no record references,
 * each with the action that re-files it. Presentational: the container reads the visit,
 * decrypts the photos and owns the dialogs.
 */
export default function PhotoPlacementPanel({ state, bytesByLocalId, onRetry, onRetryImage, onMove, onAttach }: PhotoPlacementPanelProps) {
    if (state.kind === 'loading') {
        return (
            <Box display="flex" alignItems="center" gap={1} data-testid="placement-loading">
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary"><Translate id={`${T}.loading`} /></Typography>
            </Box>
        );
    }
    if (state.kind === 'error') {
        return (
            <Alert severity="error" action={
                <Button color="inherit" size="small" onClick={onRetry} data-testid="placement-retry">
                    <Translate id={`${T}.retry`} />
                </Button>
            }>
                <Translate id={`${T}.load-error`} />
            </Alert>
        );
    }
    if (state.lesions.length === 0) {
        return <Alert severity="info" data-testid="placement-not-canonical"><Translate id={`${T}.not-canonical`} /></Alert>;
    }
    const canMove = state.lesions.length > 1;
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary"><Translate id={`${T}.intro`} /></Typography>

            <Typography variant="subtitle2"><Translate id={`${T}.lesions-title`} /></Typography>
            {state.lesions.map((lesion) => {
                const filled = lesion.slots.filter((slot) => slot.localIds.length > 0);
                return (
                    <Card key={lesion.groupRecordUuid} variant="outlined" sx={{ p: 2 }} data-testid={`placement-lesion-${lesion.groupRecordUuid}`}>
                        <Typography variant="subtitle2">{lesionTitle(lesion)}</Typography>
                        <LesionFacts lesion={lesion} />
                        {filled.length === 0 && (
                            <Typography variant="body2" sx={{ mt: 1 }}><Translate id={`${T}.lesion-no-photos`} /></Typography>
                        )}
                        {filled.map((slot) => (
                            <Box key={slot.name} sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">{slot.label}</Typography>
                                <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 0.5 }}>
                                    {slot.localIds.map((localId) => (
                                        <PhotoWithAction key={localId} bytes={bytesByLocalId[localId]}
                                            onRetry={() => onRetryImage(localId)}
                                            action={canMove && (
                                                <Button size="small" data-testid={`placement-move-${localId}`}
                                                    onClick={() => onMove({ kind: 'move', localId, fromGroupRecordUuid: lesion.groupRecordUuid, field: slot.name })}>
                                                    <Translate id={`${T}.move`} />
                                                </Button>
                                            )} />
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Card>
                );
            })}

            <Typography variant="subtitle2"><Translate id={`${T}.unassigned-title`} /></Typography>
            <Typography variant="body2" color="text.secondary"><Translate id={`${T}.unassigned-intro`} /></Typography>
            {state.unassigned.length === 0 ? (
                <Typography variant="body2" data-testid="placement-unassigned-empty"><Translate id={`${T}.unassigned-empty`} /></Typography>
            ) : (
                <Box display="flex" flexWrap="wrap" gap={2}>
                    {state.unassigned.map((image) => (
                        <PhotoWithAction key={image.localId} bytes={bytesByLocalId[image.localId]}
                            onRetry={() => onRetryImage(image.localId)}
                            action={
                                <>
                                    <Typography variant="caption" color="text.secondary">
                                        <Translate id="hospital.patient.photo-placement.uploaded-at" data={{ at: formatSubmissionDateTime(image.createdAt) }} />
                                    </Typography>
                                    <Button size="small" data-testid={`placement-attach-${image.localId}`} onClick={() => onAttach(image)}>
                                        <Translate id={`${T}.attach`} />
                                    </Button>
                                </>
                            } />
                    ))}
                </Box>
            )}
        </Box>
    );
}
